<template>
  <div>
    <VTextField
      v-model="mutableValue.client"
      :label="$t('forms.labels.client')"
      :rules="{ required: true }"
      disabled
      outlined
      @input="onInput"
    />
    <VTextField
      v-model="mutableValue.site"
      :label="$t('forms.labels.site')"
      :rules="{ required: true }"
      disabled
      outlined
      @input="onInput"
    />
    <VTextField
      v-model="name"
      :rules="{ required: true }"
      :label="$t('massBalance.modalStep.massBalanceName')"
      outlined
      @input="onInput"
      @change="emitChange"
    />
    <VSelect
      v-if="massBalanceType === SIMPLIFIED"
      v-model="indicatorType"
      :rules="{ required: true }"
      :label="$t('massBalance.simplified.indicatorType.title')"
      outlined
      :items="indicatorTypes"
      @input="onInput"
      @change="changeIndicatorType"
    />

    <div
      v-if="massBalanceType === STEAM"
      class="steamUnitStyle"
    >
      <span>{{ $t('massBalance.modalStep.selectSteamUnit') }}</span>
      <div
        class="reportUnitStyle"
      >
        <VSelect
          v-model="reportUnit.steamProductionUnit"
          :rules="{ required: true }"
          :label="$t('massBalance.modalStep.steamProductionUnit')"
          itemValue="id"
          outlined
          :items="genericSteamProdUnit"
          @input="onInput"
          @change="emitChange"
        />
      </div>
    </div>
    <div>
      <span>{{ $t('massBalance.modalStep.selectReport') }}</span>
      <div class="reportUnitStyle">
        <VCheckbox
          v-for="(r, index) in reportUnits"
          :key="index"
          v-model="reportUnit.type"
          :rules="[v => v && v.length > 0]"
          :value="r"
          :label="$t(`massBalance.modalStep.${r}`)"
          @change="emitChange"
        />
      </div>
    </div>
    <div class="reportUnitStyle">
      <VSelect
        v-model="reportUnit.massUnit"
        :rules="{ required: true }"
        :label="$t('massBalance.modalStep.massUnit')"
        itemValue="id"
        outlined
        :disabled="!reportUnit.type.includes(MASS.toLowerCase())"
        :items="genericMassUnit"
        @input="onInput"
        @change="emitChange"
      />
      <VSelect
        v-model="reportUnit.volumeUnit"
        :rules="{ required: true }"
        :label="$t('massBalance.modalStep.volumeUnit')"
        itemValue="id"
        :disabled="!reportUnit.type.includes(VOLUME.toLowerCase())"
        outlined
        :items="genericVolumeUnit"
        @input="onInput"
        @change="emitChange"
      />
    </div>
    <div>
      <VSelect
        v-if="massBalanceType === SIMPLIFIED && indicatorType === 'ppm'"
        v-model="injectionControl"
        :label="$t('massBalance.simplified.injectionControl.title')"
        itemValue="id"
        outlined
        :items="injectionControls"
        @input="onInput"
        @change="emitChange"
      />
    </div>
  </div>
</template>

<script>
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import { genericVolumes } from '@/aquassist-shared/constants/volumes';
import { mapActions } from 'vuex';
import { isEmpty } from 'lodash';
import { MASS, VOLUME, INJECTION_CONTROLS, INDICATOR_TYPES, SIMPLIFIED } from '@/aquassist-shared/constants/massBalance';
import { STEAM } from '@/aquassist-shared/constants/types/network';

export default {
    name: 'MassBalanceStepForm',
    components: {
        VSelect,
        VTextField,
    },
    props: {
        mutableValue: {
            type: Object,
            default: () => ({}),
        },
        massBalanceSelected: {
            type: Object,
            default: () => ({}),
        },
        massBalanceType: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            name: '',
            reportUnit: {
                type: [],
            },
            indicatorType: '',
            injectionControl: '',
            reportUnits: [MASS.toLowerCase(), VOLUME.toLowerCase()],
            indicatorTypes: INDICATOR_TYPES.map(t => ({ id: t, name: this.$t(`massBalance.simplified.indicatorType.${t}`) })),
            injectionControls: INJECTION_CONTROLS.map(t => ({ id: t, name: this.$t(`massBalance.simplified.injectionControl.${t}`) })),
        };
    },
    computed: {
        units() {
            return ['massUnit', 'volumeUnit', 'steamProductionUnit'];
        },
        genericMassUnit() {
            return this.getUnits('massUnit');
        },
        genericVolumeUnit() {
            return this.getUnits('volumeUnit');
        },
        genericSteamProdUnit() {
            return this.getUnits('steamUnit');
        },
        MASS() {
            return MASS;
        },
        VOLUME() {
            return VOLUME;
        },
        STEAM() {
            return STEAM;
        },
        SIMPLIFIED() {
            return SIMPLIFIED;
        },
    },
    watch: {
        'reportUnit.type': {
            handler() {
                return this.onInput();
            },
        },
    },
    created() {
        const modeEdit = !isEmpty(this.massBalanceSelected);
        if (modeEdit) {
            this.name = this.massBalanceSelected.name;
            this.reportUnit.type = this.massBalanceSelected.reportUnit.type.map(t => t.toLowerCase());
            this.indicatorType = this.massBalanceSelected?.indicatorType;
            this.injectionControl = this.massBalanceSelected?.injectionControl;
        }
        this.units.forEach(u => {
            this.reportUnit[u] = modeEdit ? this.massBalanceSelected.reportUnit[u].toLowerCase() : '';
        });
        this.onInput();
        this.emitChange();
    },
    methods: {
        ...mapActions({
            setMassBalanceStep: 'setMassBalanceStep',
        }),
        massBalanceValidate() {
            let isValid = true;
            this.reportUnit.type.forEach(v => {
                isValid = isValid && !!this.reportUnit[`${v}Unit`];
            });
            if (this.massBalanceType === STEAM) {
                isValid = isValid && !!this.reportUnit.steamProductionUnit;
            }
            return isValid && !!this.name && this.reportUnit.type.length > 0;
        },
        getUnits(type) {
            return genericVolumes.filter(v => v.tag.includes(type)).map(p => ({
                name: this.$t(`devices.productUnitMeasure.${p.name.toLowerCase()}`),
                id: p.name.toLowerCase(),
            }));
        },

        emitChange() {
            this.$emit('inputChange', this.massBalanceValidate);
        },
        onInput() {
            const data = {
                name: this.name,
                type: this.massBalanceType,
                reportUnit: {
                    type: this.reportUnit.type.map(t => t.toUpperCase()),
                    massUnit: this.reportUnit.massUnit.toUpperCase() || 'NONE',
                    volumeUnit: this.reportUnit.volumeUnit.toUpperCase() || 'NONE',
                    steamProductionUnit: this.reportUnit.steamProductionUnit.toUpperCase() || 'NONE',
                },
            };
            if (this.massBalanceType === SIMPLIFIED) {
                data.indicatorType = this.indicatorType;
                data.injectionControl = this.injectionControl;
            }
            this.setMassBalanceStep({ massBalanceStep: data });
        },
        changeIndicatorType() {
            this.$emit('changeIndicatorType', this.indicatorType);
        },
    },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/massBalanceStyle.scss";
</style>
